import { Dispatcher as BaseDispatcher } from '../../../../_common/platform/ts/BaseDispatcher';

import { IAQLFilter } from '../../../../_common/platform/tsx/filtering/Interfaces';
//import { IAQLParameters } from '../../../../_common/platform/ts/AqlUtility';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';

import { IAttributeQueryData, AttributeValueSortingType, IDataLoadEntity } from '../stores/EntityModelsStore';
import { IAQLOptions } from '../Interfaces';

export enum EntityModelsActionsConstants {
    LOAD_DATA_SOURCES,
    LOAD_DATA_SOURCES_FINISH,

    LOAD_MODELS,
    LOAD_MODELS_FINISH,

    LOAD_ENTITIES_EXTENDED_DATA,
    LOAD_ENTITIES_EXTENDED_DATA_FINISH,

    LOAD_ENTITY_VALUES,
    LOAD_ENTITY_VALUES_FINISH,

    LOAD_TREE_ENTITY_VALUES,
    LOAD_TREE_ENTITY_VALUES_FINISH,

    LOAD_MODEL_LEAF_ON_DEMAND,

    CLEAN_CACHE
}

export module EntityModelsActionInterfaces {
    export interface IAction {
        actionType: EntityModelsActionsConstants;
    }

    export interface ILoadModels extends IAction {
        dataSources: number[];
        reload: boolean;
    }

    export interface ILoadModelsFinish extends IAction {
        dataSources: number[];
    }

    export interface ILoadEntityValues extends IAction {
        dataSourceID: number;
        attributeData: IAttributeQueryData;
        theme: IValuesCollection;
        filter: IAQLFilter[];
    }

    export interface ILoadEntityValuesFinish extends IAction {
        collectionKey: string;
    }

    export interface ILoadEntitiesExtendedDataQuery {
        dataSourceID: number;
        entityNames: string[];
    }

    export interface ILoadEntitiesExtendedData extends IAction {
        dataQueries: ILoadEntitiesExtendedDataQuery[];
        refresh: boolean;
        runTimeWidgetID: number;
    }

    export interface ILoadTreeEntityValues extends IAction {
        dataSourceID: number;
        attribute: string;
        parentAttribute: string;
        options: IAQLOptions;
        theme: IValuesCollection;
        rootNodeID: string;
        labelAttribute: string;
        filter: IAQLFilter[];
        limit: number;
        ignoreCache: boolean;
        sortingType?: AttributeValueSortingType;
    }

    export interface ILoadTreeEntityValuesFinish extends IAction {
        collectionKey: string;
    }

    export interface ILoadModelLeafOnDemand extends IAction {
        entityName: string;
        dataSourceID: number;
    }

    export interface ILoadDataSources extends IAction {
        reload: boolean;
    }
}

export function checkAction<T extends EntityModelsActionInterfaces.IAction>(
    action: EntityModelsActionInterfaces.IAction,
    id: EntityModelsActionsConstants
): action is T {
    return action.actionType === id;
}

export class EntityModelsDispatcher extends BaseDispatcher {
    /**
     * A bridge function between the views and the dispatcher, marking the action
     * as a view action.  Another variant here could be handleServerAction.
     */
    public handleViewAction<T>(action: T) {
        this.dispatch({
            source: 'VIEW_ACTION',
            action: action
        });
    }

    public static get() {
        return new EntityModelsDispatcher();
    }
}

export class EntityModelsActions {
    constructor(private dispatcher: EntityModelsDispatcher) {}

    public static get(dispatcher: EntityModelsDispatcher) {
        return new EntityModelsActions(dispatcher);
    }

    public loadModels(dataSources?: number[], reload?: boolean) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadModels>({
            actionType: EntityModelsActionsConstants.LOAD_MODELS,
            dataSources: dataSources,
            reload: reload
        });
    }

    public loadModelFinish(dataSources: number[]) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadModelsFinish>({
            actionType: EntityModelsActionsConstants.LOAD_MODELS_FINISH,
            dataSources: dataSources
        });
    }

    public loadEntityValues(
        dataSourceID: number,
        theme: IValuesCollection,
        attributeData: IAttributeQueryData,
        filter?: IAQLFilter[]
    ) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadEntityValues>({
            actionType: EntityModelsActionsConstants.LOAD_ENTITY_VALUES,
            dataSourceID: dataSourceID,
            attributeData: attributeData,
            theme: theme,
            filter: filter
        });
    }

    public loadEntityValuesFinish(collectionKey: string) {
        this.dispatcher.handleViewAction({
            actionType: EntityModelsActionsConstants.LOAD_ENTITY_VALUES_FINISH,
            collectionKey: collectionKey
        });
    }

    public loadEntityExtendedData(
        dataQueries: EntityModelsActionInterfaces.ILoadEntitiesExtendedDataQuery[],
        refresh?: boolean,
        runTimeWidgetID?: number
    ) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadEntitiesExtendedData>({
            actionType: EntityModelsActionsConstants.LOAD_ENTITIES_EXTENDED_DATA,
            dataQueries: dataQueries,
            refresh: refresh,
            runTimeWidgetID: runTimeWidgetID
        });
    }

    public loadEntitiesExtendedDataFinish() {
        this.dispatcher.handleViewAction({
            actionType: EntityModelsActionsConstants.LOAD_ENTITIES_EXTENDED_DATA_FINISH
        });
    }

    public loadTreeEntityValues(
        dataSourceID: number,
        attribute: string,
        parentAttribute: string,
        options: IAQLOptions,
        theme: IValuesCollection,
        rootNodeID?: string,
        labelAttribute?: string,
        filter?: IAQLFilter[],
        limit?: number,
        ignoreCache?: boolean,
        sortingType?: AttributeValueSortingType
    ) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadTreeEntityValues>({
            actionType: EntityModelsActionsConstants.LOAD_TREE_ENTITY_VALUES,
            dataSourceID: dataSourceID,
            attribute: attribute,
            parentAttribute: parentAttribute,
            options: options,
            theme: theme,
            rootNodeID: rootNodeID,
            labelAttribute: labelAttribute,
            filter: filter,
            sortingType: sortingType,
            limit: limit,
            ignoreCache: ignoreCache
        });
    }

    public loadTreeEntityValuesFinish(collectionKey: string) {
        this.dispatcher.handleViewAction({
            actionType: EntityModelsActionsConstants.LOAD_TREE_ENTITY_VALUES_FINISH,
            collectionKey: collectionKey
        });
    }

    public loadModelLeafOnDemand(entityName: string, dataSourceID: number) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadModelLeafOnDemand>({
            actionType: EntityModelsActionsConstants.LOAD_MODEL_LEAF_ON_DEMAND,
            entityName: entityName,
            dataSourceID: dataSourceID
        });
    }

    public loadDataSources(reload?: boolean) {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.ILoadDataSources>({
            actionType: EntityModelsActionsConstants.LOAD_DATA_SOURCES,
            reload: reload
        });
    }

    public loadDataSourcesFinish() {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.IAction>({
            actionType: EntityModelsActionsConstants.LOAD_DATA_SOURCES_FINISH
        });
    }

    public cleanCache() {
        this.dispatcher.handleViewAction<EntityModelsActionInterfaces.IAction>({
            actionType: EntityModelsActionsConstants.CLEAN_CACHE
        });
    }
}
